import vcard from "vcf";
import { ImageSchema, type Image } from "#gen/bundles/v1/common_pb.js";
import { ContactDataSchema, SocialLinkSchema, SocialLinkType, type ContactData, type SocialLink } from "#gen/bundles/v1/contact_pb.js";
import { PostalAddressSchema, type PostalAddress } from "#gen/google/type/postal_address_pb.js";
import { blobToBase64 } from "#lib/blob.js";
import { resizeURL } from "#lib/image.js";
import { normalizePhoneNumber } from "#lib/phonenumber.js";
import { create } from "#lib/protobuf-compat.js";
import { getSocialName } from "#lib/social.js";

interface VCardField {
	name: string;
	key: string;
}

const PHOTO_SIZE = 512;
const PHOTO_TIMEOUT_MS = 2500;

const socialTypes: Set<string> = new Set(["facebook", "twitter", "x", "linkedin", "instagram", "youtube", "snapchat", "whatsapp", "discord", "twitch", "tiktok", "spotify", "soundcloud", "apple-music", "apple-podcast"]);

export const VCARD_FIELDS: Record<string, VCardField[]> = {
	photo: [
		{
			name: "Your photo",
			key: "photo",
		},
		{
			name: "Name",
			key: "name",
		},
		{
			name: "Company",
			key: "company",
		},
		{
			name: "Department",
			key: "department",
		},
		{
			name: "Title",
			key: "title",
		},
	],
	contact: [
		{
			name: "Work number",
			key: "workNumber",
		},
		{
			name: "Mobile number",
			key: "mobileNumber",
		},
		{
			name: "Email",
			key: "email",
		},
		{
			name: "Website",
			key: "website",
		},
		{
			name: "Address",
			key: "address",
		},
	],
	social: Array.from(socialTypes).map(type => {
		return {
			name: getSocialName(toSocialLinkType(type)),
			key: type,
		};
	}),
};

export async function encode(data: ContactData, nonExportedFields: Set<string>): Promise<string> {
	const card = new vcard();
	if (!nonExportedFields.has("name")) {
		setName(card, data.fullName);
	}
	if (data.photo && !nonExportedFields.has("photo")) {
		await setPhoto(card, data.photo);
	}
	if (data.email && !nonExportedFields.has("email")) {
		setEmail(card, "work", data.email);
	}
	if (data.workNumber && !nonExportedFields.has("workNumber")) {
		setPhone(card, "work", data.workNumber);
	}
	if (data.mobileNumber && !nonExportedFields.has("mobileNumber")) {
		setPhone(card, "cell", data.mobileNumber);
	}
	if (data.website && !nonExportedFields.has("website")) {
		setWebsite(card, data.website);
	}
	if (data.address && !nonExportedFields.has("address")) {
		setAddress(card, "work", data.address);
	}
	if ((data.company && !nonExportedFields.has("company")) || (data.department && !nonExportedFields.has("department"))) {
		setCompany(card, !nonExportedFields.has("company") ? data.company : "", !nonExportedFields.has("department") ? data.department : "");
	}
	if (data.title && !nonExportedFields.has("title")) {
		setTitle(card, data.title);
	}
	if (data.socialLinks) {
		for (const link of data.socialLinks) {
			const type = toVCardSocialType(link.type);
			if (!nonExportedFields.has(type)) {
				card.add("xSocialprofile", link.id, { type });
			}
		}
	}
	return card.toString("3.0");
}

export function parse(str: string): ContactData {
	const data = create(ContactDataSchema);
	const cards = vcard.parse(str.replaceAll(/\r\n|\r|\n/g, "\r\n"));
	if (cards.length === 0) {
		return data;
	}
	const card = cards[0];
	data.photo = getPhoto(card);
	data.fullName = getName(card);
	data.email = getEmail(card, "work") || getEmail(card, "home") || getEmail(card, "pref");
	data.workNumber = getPhone(card, "work");
	data.mobileNumber = getPhone(card, "cell");
	data.website = getWebsite(card);
	data.address = getAddress(card, "work") || getAddress(card, "home");
	data.company = getCompany(card);
	data.department = getDepartment(card);
	data.title = getTitle(card);
	data.socialLinks = getSocialLinks(card);
	return data;
}

function getName(card: vcard): string {
	let prop = card.get("fn");
	if (prop) {
		return prop.valueOf() as string;
	}
	prop = card.get("n");
	if (prop) {
		return parseName(prop.valueOf() as string);
	}
	return "";
}

function setName(card: vcard, name: string) {
	card.add("fn", name);
	card.add("n", encodeName(name));
}

function getPhoto(card: vcard): Image | undefined {
	const prop = card.get("photo");
	if (prop) {
		const image = create(ImageSchema);
		if ("encoding" in prop && String(prop.encoding).toLowerCase() === "b") {
			image.url = `data:image/${"type" in prop ? prop.type : "jpeg"};base64,${prop.valueOf() as string}`;
		} else {
			image.url = prop.valueOf() as string;
		}
		return image;
	}
	return undefined;
}

async function setPhoto(card: vcard, photo: Image) {
	if (!photo.url || photo.url.includes("{{")) {
		return;
	}
	try {
		const { data, type } = await getPhotoData(photo.url);
		card.set("photo", data, { encoding: "b", type });
	} catch {
		card.set("photo", photo.url, { type: "jpeg" });
	}
}

async function getPhotoData(url: string): Promise<{ data: string; type: string }> {
	const dataUrl = /^data:image\/(\w+);base64,(.+)$/i.exec(url);
	if (dataUrl) {
		return { data: dataUrl[2], type: dataUrl[1].toUpperCase() };
	}
	const response = await fetch(resizeURL(url, { width: PHOTO_SIZE, height: PHOTO_SIZE, fit: "scale-down", f: "jpeg", background: "white" }), { signal: AbortSignal.timeout(PHOTO_TIMEOUT_MS) });
	if (!response.ok) {
		throw new Error(`Failed to load photo: ${response.status}`);
	}
	const data = (await blobToBase64(await response.blob())).replace(/^data:.*?;base64,/, "");
	if (!data) {
		throw new Error("Empty photo");
	}
	return { data, type: "JPEG" };
}

function getEmail(card: vcard, type: string): string {
	const props = card.get("email");
	return valueOf(props, type);
}

function setEmail(card: vcard, type: string, email: string) {
	card.add("email", email, { type });
}

function getPhone(card: vcard, type: string): string {
	const props = card.get("tel");
	return normalizePhoneNumber(valueOf(props, type));
}

function setPhone(card: vcard, type: string, phone: string) {
	card.add("tel", phone, { type });
}

function getWebsite(card: vcard): string {
	const prop = card.get("url");
	if (prop) {
		return card.get("url").valueOf() as string;
	}
	return "";
}

function setWebsite(card: vcard, website: string) {
	card.add("url", website);
}

function getCompany(card: vcard): string {
	const prop = card.get("org");
	if (prop) {
		return parseOrg(card.get("org").valueOf() as string)[0];
	}
	return "";
}

function getDepartment(card: vcard): string {
	const prop = card.get("org");
	if (prop) {
		return parseOrg(card.get("org").valueOf() as string)[1] ?? "";
	}
	return "";
}

function setCompany(card: vcard, company: string, department?: string) {
	const parts = [company];
	if (department) {
		parts.push(department);
	}
	card.add("org", enocdeOrg(parts));
}

function getTitle(card: vcard): string {
	const prop = card.get("title");
	if (prop) {
		return prop.valueOf() as string;
	}
	return "";
}

function setTitle(card: vcard, title: string) {
	card.add("title", title);
}

function getAddress(card: vcard, type: string): PostalAddress | undefined {
	const props = card.get("adr");
	const value = valueOf(props, type);
	if (value) {
		return parseAddress(value);
	}
	return undefined;
}

function setAddress(vcard: vcard, type: string, address: PostalAddress) {
	vcard.add("adr", encodeAddress(address), { type });
}

function getSocialLinks(card: vcard): SocialLink[] {
	const props = card.get("xSocialprofile");
	if (!props) {
		return [];
	}
	if (Array.isArray(props)) {
		return props.filter(prop => "type" in prop && socialTypes.has(prop.type as string)).map(prop => toSocialLink(prop));
	}
	if ("type" in props && socialTypes.has(props.type as string)) {
		return [toSocialLink(props)];
	}
	return [];
}

function toSocialLink(prop: vcard.Property): SocialLink {
	const type = "type" in prop ? (prop.type as string) : "";
	return create(SocialLinkSchema, { type: toSocialLinkType(type), id: prop.valueOf() as string });
}

function toSocialLinkType(type: string): SocialLinkType {
	switch (type) {
		case "facebook":
			return SocialLinkType.FACEBOOK;
		case "twitter":
		case "x":
			return SocialLinkType.X;
		case "linkedin":
			return SocialLinkType.LINKEDIN;
		case "instagram":
			return SocialLinkType.INSTAGRAM;
		case "youtube":
			return SocialLinkType.YOUTUBE;
		case "snapchat":
			return SocialLinkType.SNAPCHAT;
		case "whatsapp":
			return SocialLinkType.WHATSAPP;
		case "discord":
			return SocialLinkType.DISCORD;
		case "twitch":
			return SocialLinkType.TWITCH;
		case "tiktok":
			return SocialLinkType.TIKTOK;
		case "spotify":
			return SocialLinkType.SPOTIFY;
		case "soundcloud":
			return SocialLinkType.SOUNDCLOUD;
		case "apple-music":
			return SocialLinkType.APPLE_MUSIC;
	}
	return SocialLinkType.UNSPECIFIED;
}

export function toVCardSocialType(type: SocialLinkType): string {
	switch (type) {
		case SocialLinkType.FACEBOOK:
			return "facebook";
		case SocialLinkType.X:
			return "x";
		case SocialLinkType.LINKEDIN:
			return "linkedin";
		case SocialLinkType.INSTAGRAM:
			return "instagram";
		case SocialLinkType.YOUTUBE:
			return "youtube";
		case SocialLinkType.SNAPCHAT:
			return "snapchat";
		case SocialLinkType.WHATSAPP:
			return "whatsapp";
		case SocialLinkType.DISCORD:
			return "discord";
		case SocialLinkType.TWITCH:
			return "twitch";
		case SocialLinkType.TIKTOK:
			return "tiktok";
		case SocialLinkType.SPOTIFY:
			return "spotify";
		case SocialLinkType.SOUNDCLOUD:
			return "soundcloud";
		case SocialLinkType.APPLE_MUSIC:
			return "apple-music";
	}
	return "";
}

function valueOf(props: vcard.Property | vcard.Property[], type: string): string {
	if (!props) {
		return "";
	}
	if (Array.isArray(props)) {
		for (const prop of props) {
			if (prop.is(type)) {
				return prop.valueOf() as string;
			}
		}
	} else if (props.is(type)) {
		return props.valueOf() as string;
	}
	return "";
}

function parseName(str: string): string {
	const parts = str.split(";");
	return parts.slice(0, 2).reverse().join(" ");
}

function encodeName(name: string): string {
	const parts = name.split(" ");
	return `${parts[1] ?? ""};${parts[0]};;`;
}

function parseOrg(str: string): string[] {
	return str.split(";");
}

function enocdeOrg(org: string[]): string {
	return org.join(";");
}

function parseAddress(str: string): PostalAddress {
	const address = create(PostalAddressSchema);
	const parts = str.split(";");
	address.addressLines = parts[2].split("\\n");
	address.locality = parts[3];
	address.administrativeArea = parts[4];
	address.postalCode = parts[5];
	address.regionCode = parts[6];
	return address;
}

function encodeAddress(address: PostalAddress): string {
	return `;;${address.addressLines.join("\\n")};${address.locality};${address.administrativeArea};${address.postalCode};${address.regionCode}`;
}
